// ============================================
// 八字计算工具 - 前端版本
// ============================================

/**
 * 计算初始化时应该选中的大运、流年、流月、流日、流时
 * 如果当前时间在大运范围内，则选中当前时间
 * 如果当前时间不在大运范围内，则所有项目默认选中第一个
 * @param {Object} data - 八字排盘数据
 * @param {Date} [now] - 当前时间
 * @returns {Object} 各级别索引
 */
export function getCurrentTimeSelection(data, now = new Date()) {
  if (!data) return {};

  const currentYear = now.getFullYear();
  const currentHour = now.getHours();

  // 计算时辰索引 (0-11，对应子丑寅卯...)
  const shiChenIndex = Math.floor(((currentHour + 1) % 24) / 2);

  const daYun = data.da_yun || {};

  // 获取大运年份列表
  const daYunYears = Object.keys(daYun)
    .map((y) => Number(y))
    .sort((a, b) => a - b);

  if (daYunYears.length === 0) {
    return {};
  }

  // 确定当前是否在大运范围内
  // 每个大运10年，例如1990-2000, 2000-2010等
  let isInDaYunRange = false;
  let currentDaYun = null;

  for (let i = 0; i < daYunYears.length; i++) {
    const year = daYunYears[i];
    // 当前大运范围：[year, year + 10)
    if (currentYear >= year && currentYear < year + 10) {
      isInDaYunRange = true;
      currentDaYun = year;
      break;
    }
  }

  if (isInDaYunRange) {
    // 当前时间在大运范围内，使用当前时间
    return {
      isInDaYunRange: true,
      currentDaYun,
      currentLiuNian: currentYear,
      currentLiuYue: null, // 待计算
      currentLiuRi: null, // 待计算
      currentLiuShi: shiChenIndex,
    };
  } else {
    // 当前时间不在大运范围内，所有项目默认选中第一个
    const firstDaYun = daYunYears[0];
    return {
      isInDaYunRange: false,
      currentDaYun: firstDaYun,
      currentLiuNian: firstDaYun, // 第一个流年（第一年）
      currentLiuYue: 1, // 第一个月（正月）
      currentLiuRi: null, // 第一天，由组件根据节气数据自动选择
      currentLiuShi: 0, // 第一个时辰（子时）
    };
  }
}

// 天干列表
const TIAN_GAN = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"];

// 地支列表
const DI_ZHI = ["子", "丑", "寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥"];

// 地支藏干表
const ZHI_CANG_GAN = {
  "子": ["癸"],
  "丑": ["己", "癸", "辛"],
  "寅": ["甲", "丙", "戊"],
  "卯": ["乙"],
  "辰": ["戊", "乙", "癸"],
  "巳": ["丙", "戊", "庚"],
  "午": ["丁", "己"],
  "未": ["己", "丁", "乙"],
  "申": ["庚", "壬", "戊"],
  "酉": ["辛"],
  "戌": ["戊", "辛", "丁"],
  "亥": ["壬", "甲"]
};

// 十神关系表（以日元为主）
const SHI_SHEN_MAP = {
  "甲": { "甲": "比肩", "乙": "劫财", "丙": "食神", "丁": "伤官", "戊": "偏财", "己": "正财", "庚": "七杀", "辛": "正官", "壬": "偏印", "癸": "正印" },
  "乙": { "甲": "劫财", "乙": "比肩", "丙": "伤官", "丁": "食神", "戊": "正财", "己": "偏财", "庚": "正官", "辛": "七杀", "壬": "正印", "癸": "偏印" },
  "丙": { "甲": "偏印", "乙": "正印", "丙": "比肩", "丁": "劫财", "戊": "食神", "己": "伤官", "庚": "偏财", "辛": "正财", "壬": "七杀", "癸": "正官" },
  "丁": { "甲": "正印", "乙": "偏印", "丙": "劫财", "丁": "比肩", "戊": "伤官", "己": "食神", "庚": "正财", "辛": "偏财", "壬": "正官", "癸": "七杀" },
  "戊": { "甲": "七杀", "乙": "正官", "丙": "偏印", "丁": "正印", "戊": "比肩", "己": "劫财", "庚": "食神", "辛": "伤官", "壬": "偏财", "癸": "正财" },
  "己": { "甲": "正官", "乙": "七杀", "丙": "正印", "丁": "偏印", "戊": "劫财", "己": "比肩", "庚": "伤官", "辛": "食神", "壬": "正财", "癸": "偏财" },
  "庚": { "甲": "偏财", "乙": "正财", "丙": "七杀", "丁": "正官", "戊": "偏印", "己": "正印", "庚": "比肩", "辛": "劫财", "壬": "食神", "癸": "伤官" },
  "辛": { "甲": "正财", "乙": "偏财", "丙": "正官", "丁": "七杀", "戊": "正印", "己": "偏印", "庚": "劫财", "辛": "比肩", "壬": "伤官", "癸": "食神" },
  "壬": { "甲": "食神", "乙": "伤官", "丙": "偏财", "丁": "正财", "戊": "七杀", "己": "正官", "庚": "偏印", "辛": "正印", "壬": "比肩", "癸": "劫财" },
  "癸": { "甲": "伤官", "乙": "食神", "丙": "正财", "丁": "偏财", "戊": "正官", "己": "七杀", "庚": "正印", "辛": "偏印", "壬": "劫财", "癸": "比肩" }
};

// 五虎遁：年干 -> 正月（寅月）起干
const WU_HU_DUN = {
  "甲": "丙", "己": "丙",
  "乙": "戊", "庚": "戊",
  "丙": "庚", "辛": "庚",
  "丁": "壬", "壬": "壬",
  "戊": "甲", "癸": "甲"
};

// 五鼠遁：日干 -> 子时起干
const WU_SHU_DUN = {
  "甲": "甲", "己": "甲",
  "乙": "丙", "庚": "丙",
  "丙": "戊", "辛": "戊",
  "丁": "庚", "壬": "庚",
  "戊": "壬", "癸": "壬"
};

// 甲子日基准（2025-12-21是甲子日）
const JIAZI_BASE = new Date(2025, 11, 21);

/**
 * 计算十神
 * @param {string} yuanGan - 日元
 * @param {string} gan - 要计算的天干
 * @returns {string} 十神名称
 */
export function getShiShen(yuanGan, gan) {
  return SHI_SHEN_MAP[yuanGan]?.[gan] || "";
}

/**
 * 计算一柱的完整信息
 * @param {string} yuanGan - 日元
 * @param {string} gan - 天干
 * @param {string} zhi - 地支
 * @returns {Object} 柱信息
 */
export function getZhuInfo(yuanGan, gan, zhi) {
  const cangGanList = ZHI_CANG_GAN[zhi] || [];
  return {
    gan,
    zhi,
    zhi_canggan: cangGanList,
    gan_shen: getShiShen(yuanGan, gan),
    zhi_shen: cangGanList.map(cg => getShiShen(yuanGan, cg))
  };
}

/**
 * 根据年份计算年干支
 * 1984年是甲子年
 * @param {number} year
 * @returns {[string, string]} [天干, 地支]
 */
export function getNianGanZhi(year) {
  const offset = (year - 4) % 60;
  const ganIdx = offset % 10;
  const zhiIdx = offset % 12;
  return [TIAN_GAN[ganIdx], DI_ZHI[zhiIdx]];
}

/**
 * 计算流月（一年的12个月）
 * @param {string} yuanGan - 日元
 * @param {number} year - 年份
 * @returns {Object} 12个月的柱信息，key为月份(1-12)
 */
export function getLiuYue(yuanGan, year) {
  const [nianGan] = getNianGanZhi(year);
  const startGan = WU_HU_DUN[nianGan]; // 正月寅月的干
  const startGanIdx = TIAN_GAN.indexOf(startGan);
  
  const liuYue = {};
  for (let month = 1; month <= 12; month++) {
    // 寅月是正月(1月)，所以地支索引 = (month + 1) % 12
    const zhiIdx = (month + 1) % 12;
    const zhi = DI_ZHI[zhiIdx];
    
    // 月干 = 起始干 + (月份-1)
    const ganIdx = (startGanIdx + month - 1) % 10;
    const gan = TIAN_GAN[ganIdx];
    
    liuYue[month] = getZhuInfo(yuanGan, gan, zhi);
  }
  return liuYue;
}

/**
 * 计算指定日期范围的流日
 * @param {string} yuanGan - 日元
 * @param {Date} startDate - 开始日期（包含）
 * @param {Date} endDate - 结束日期（不包含）
 * @returns {Object} 每日的柱信息，key为"M月D日"格式
 */
export function getLiuRi(yuanGan, startDate, endDate) {
  const liuRi = {};
  const currentDate = new Date(startDate);
  
  while (currentDate < endDate) {
    const diffTime = currentDate.getTime() - JIAZI_BASE.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const ganIdx = ((diffDays % 10) + 10) % 10;
    const zhiIdx = ((diffDays % 12) + 12) % 12;
    
    const gan = TIAN_GAN[ganIdx];
    const zhi = DI_ZHI[zhiIdx];
    
    const month = currentDate.getMonth() + 1;
    const day = currentDate.getDate();
    const key = `${month}月${day}日`;
    
    liuRi[key] = getZhuInfo(yuanGan, gan, zhi);
    
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return liuRi;
}

/**
 * 计算流时（一天的所有时辰）
 * @param {string} yuanGan - 日元
 * @param {string} riGan - 日干（用于五鼠遁）
 * @returns {Object} 12个时辰的柱信息，key为时辰索引(0-11)
 */
export function getLiuShi(yuanGan, riGan) {
  const startGan = WU_SHU_DUN[riGan]; // 子时的干
  const startGanIdx = TIAN_GAN.indexOf(startGan);
  
  const liuShi = {};
  for (let i = 0; i < 12; i++) {
    const ganIdx = (startGanIdx + i) % 10;
    const gan = TIAN_GAN[ganIdx];
    const zhi = DI_ZHI[i]; // 子丑寅卯...
    
    liuShi[i] = {
      ...getZhuInfo(yuanGan, gan, zhi),
      shiChen: zhi
    };
  }
  return liuShi;
}

/**
 * 获取一年完整的流月流日流时信息（按节气划分）
 * @param {string} yuanGan - 日元
 * @param {number} year - 年份
 * @param {Object} jieQiData - 节气数据（从后端获取的12节气UTC时间）
 * @returns {Object} 包含流月、流日、流时的完整信息，按农历月份(1-12)组织
 */
export function getFullYearInfo(yuanGan, year, jieQiData) {
  const liuYue = getLiuYue(yuanGan, year);
  const result = {};
  
  if (!jieQiData) {
    // 如果没有节气数据，返回空结构
    for (let month = 1; month <= 12; month++) {
      result[month] = {
        ...liuYue[month],
        liuRi: {}
      };
    }
    return result;
  }
  
  // 获取12个节气的日期（转换为本地日期）
  const jieDates = JIE_NAMES.map((name, index) => {
    const utcStr = jieQiData[name];
    if (!utcStr) return null;
    const date = new Date(utcStr);
    // 转为本地时间（UTC+8）
    return new Date(date.getTime() + 8 * 60 * 60 * 1000);
  });
  
  for (let month = 1; month <= 12; month++) {
    const startDate = jieDates[month - 1];
    const endDate = jieDates[month] || new Date(year + 1, 0, 1); // 腊月到小寒，小寒到下一年立春
    
    let liuRi = {};
    if (startDate && endDate) {
      liuRi = getLiuRi(yuanGan, startDate, endDate);
    }
    
    const riList = {};
    for (const [dayKey, zhuInfo] of Object.entries(liuRi)) {
      riList[dayKey] = {
        ...zhuInfo,
        liuShi: getLiuShi(yuanGan, zhuInfo.gan)
      };
    }
    
    result[month] = {
      ...liuYue[month],
      liuRi: riList
    };
  }
  
  return result;
}

/**
 * 获取大运下的所有流年（带流月、流日、流时完整信息）
 * @param {string} yuanGan - 日元
 * @param {number} daYunYear - 大运起始年份
 * @param {Object} jieQiCache - 节气数据缓存
 * @returns {Object} 10年的流年信息
 */
export function getLiuNianByDaYun(yuanGan, daYunYear, jieQiCache = {}) {
  const liuNian = {};
  for (let i = 0; i < 10; i++) {
    const year = daYunYear + i;
    const [gan, zhi] = getNianGanZhi(year);
    liuNian[year] = {
      ...getZhuInfo(yuanGan, gan, zhi),
      liuYue: getFullYearInfo(yuanGan, year, jieQiCache[year])
    };
  }
  return liuNian;
}

// 时辰名称（用于显示）
export const SHI_CHEN_NAMES = ["子时", "丑时", "寅时", "卯时", "辰时", "巳时", "午时", "未时", "申时", "酉时", "戌时", "亥时"];

// 月份名称
export const YUE_NAMES = ["正月", "二月", "三月", "四月", "五月", "六月", "七月", "八月", "九月", "十月", "冬月", "腊月"];

// 十二节（月令）名称
const JIE_NAMES = [
  "立春",   // 寅月（正月）
  "惊蛰",   // 卯月（二月）
  "清明",   // 辰月（三月）
  "立夏",   // 巳月（四月）
  "芒种",   // 午月（五月）
  "小暑",   // 未月（六月）
  "立秋",   // 申月（七月）
  "白露",   // 酉月（八月）
  "寒露",   // 戌月（九月）
  "立冬",   // 亥月（十月）
  "大雪",   // 子月（冬月）
  "小寒",   // 丑月（腊月）
];

// 缓存节气数据
let jieQiCache = {};

/**
 * 获取指定月份的节气名称
 * @param {number} month - 月份(1-12)
 * @returns {string} 节气名称
 */
export function getJieQiNameByMonth(month) {
  return JIE_NAMES[month - 1] || "";
}

/**
 * 从后端获取指定年份的十二节气日期
 * @param {number} year - 年份
 * @returns {Promise<Object>} 节气日期数据
 */
export async function fetchJieQiDates(year) {
  // 检查缓存
  if (jieQiCache[year]) {
    return jieQiCache[year];
  }

  try {
    const apiBase = process.env.REACT_APP_API_URL || "/api";
    const response = await fetch(`${apiBase}/jieqi?year=${year}`);
    if (!response.ok) {
      throw new Error("Failed to fetch jieqi data");
    }
    const data = await response.json();
    // 缓存结果
    jieQiCache[year] = data;
    return data;
  } catch (error) {
    console.error("Error fetching jieqi:", error);
    return null;
  }
}

/**
 * 将 UTC 时间字符串转换为北京时间的月和日
 * @param {string} utcStr - ISO 格式的 UTC 时间字符串
 * @returns {Object} { month, day }
 */
function utcToBeijingMonthDay(utcStr) {
  const date = new Date(utcStr);
  // 转为北京时间 (+8小时)
  const beijingTime = new Date(date.getTime() + 8 * 60 * 60 * 1000);
  return {
    month: beijingTime.getUTCMonth() + 1, // 0-based to 1-based
    day: beijingTime.getUTCDate()
  };
}

/**
 * 获取指定年份月份的节气日期
 * @param {number} year - 年份
 * @param {number} month - 月份(1-12)
 * @param {Object} jieQiData - 从后端获取的节气数据 (UTC ISO 字符串)
 * @returns {Object} 节气名称和日期 { name, month, day, loading }
 */
export function getJieQiByMonth(year, month, jieQiData) {
  const name = JIE_NAMES[month - 1];
  
  if (!jieQiData) {
    // 数据未加载，返回加载状态
    return { name, month: 0, day: 0, loading: true };
  }

  const utcStr = jieQiData[name];
  if (utcStr) {
    const { month: bjMonth, day: bjDay } = utcToBeijingMonthDay(utcStr);
    return {
      name,
      month: bjMonth,
      day: bjDay,
      loading: false,
    };
  }

  return { name, month: 0, day: 0, loading: false };
}

/**
 * 根据当前日期和节气数据，确定当前日期属于哪个农历月份
 * @param {Date} date - 当前日期（本地时间）
 * @param {Object} jieQiData - 节气数据（UTC ISO 字符串）
 * @returns {Object} { lunarMonth: 1-12, dayKey: "M月D日" }
 */
export function getLunarMonthByDate(date, jieQiData) {
  if (!jieQiData) {
    // 没有节气数据，返回公历月份
    return {
      lunarMonth: date.getMonth() + 1,
      dayKey: `${date.getMonth() + 1}月${date.getDate()}日`
    };
  }
  
  // 将当前日期转为北京时间（年月日）用于比较
  // 由于节气数据已经是北京时间，我们需要统一比较基准
  const bjDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
  const year = bjDate.getUTCFullYear();
  const month = bjDate.getUTCMonth();
  const day = bjDate.getUTCDate();
  
  // 获取12个节气的日期（转换为北京时间的年月日）
  const jieDates = JIE_NAMES.map((name) => {
    const utcStr = jieQiData[name];
    if (!utcStr) return null;
    const d = new Date(utcStr);
    // 转为北京时间
    const bjD = new Date(d.getTime() + 8 * 60 * 60 * 1000);
    return {
      year: bjD.getUTCFullYear(),
      month: bjD.getUTCMonth(),
      day: bjD.getUTCDate(),
      time: bjD.getTime()
    };
  });
  
  // 当前日期的北京时间时间戳（只比较日期部分）
  const currentDateTime = new Date(year, month, day).getTime();
  
  // 找到当前日期所在的节气区间
  for (let i = 0; i < 12; i++) {
    const startJie = jieDates[i];
    const endJie = jieDates[i + 1];
    
    if (!startJie) continue;
    
    const startTime = new Date(startJie.year, startJie.month, startJie.day).getTime();
    
    let endTime;
    if (endJie) {
      endTime = new Date(endJie.year, endJie.month, endJie.day).getTime();
    } else {
      // 最后一个月（腊月）到小寒，小寒在下一年
      endTime = new Date(year + 1, 0, 1).getTime();
    }
    
    if (currentDateTime >= startTime && currentDateTime < endTime) {
      return {
        lunarMonth: i + 1, // 农历月份 1-12
        dayKey: `${month + 1}月${day}日`
      };
    }
  }
  
  // 如果找不到（在立春之前），返回腊月
  const liChun = jieDates[0];
  if (liChun) {
    const liChunTime = new Date(liChun.year, liChun.month, liChun.day).getTime();
    if (currentDateTime < liChunTime) {
      return {
        lunarMonth: 12, // 腊月
        dayKey: `${month + 1}月${day}日`
      };
    }
  }
  
  // 默认返回公历月份
  return {
    lunarMonth: month + 1,
    dayKey: `${month + 1}月${day}日`
  };
}

/**
 * 获取指定年月的月柱信息
 * @param {string} yuanGan - 日元
 * @param {number} year - 年份
 * @param {number} month - 月份(1-12)
 * @returns {Object} 月柱信息
 */
export function getYueZhuByYearMonth(yuanGan, year, month) {
  const [nianGan] = getNianGanZhi(year);
  const startGan = WU_HU_DUN[nianGan];
  const startGanIdx = TIAN_GAN.indexOf(startGan);
  
  const zhiIdx = (month + 1) % 12;
  const zhi = DI_ZHI[zhiIdx];
  
  const ganIdx = (startGanIdx + month - 1) % 10;
  const gan = TIAN_GAN[ganIdx];
  
  return getZhuInfo(yuanGan, gan, zhi);
}

/**
 * 获取指定年月日的日柱信息
 * @param {string} yuanGan - 日元
 * @param {number} year - 年份
 * @param {number|string} month - 月份(1-12)，或"M月D日"格式的完整日期字符串
 * @param {number} day - 日期(1-31)，如果month是字符串则此参数忽略
 * @returns {Object} 日柱信息
 */
export function getRiZhuByDate(yuanGan, year, month, day) {
  let date;
  
  if (typeof month === 'string' && month.includes('月')) {
    // month 是 "M月D日" 格式，如 "2月4日"
    const match = month.match(/(\d+)月(\d+)日/);
    if (match) {
      const m = parseInt(match[1], 10);
      const d = parseInt(match[2], 10);
      date = new Date(year, m - 1, d);
    } else {
      throw new Error('Invalid date format: ' + month);
    }
  } else {
    // 传统数字参数
    date = new Date(year, month - 1, day);
  }
  
  const diffTime = date.getTime() - JIAZI_BASE.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  const ganIdx = ((diffDays % 10) + 10) % 10;
  const zhiIdx = ((diffDays % 12) + 12) % 12;
  
  const gan = TIAN_GAN[ganIdx];
  const zhi = DI_ZHI[zhiIdx];
  
  return getZhuInfo(yuanGan, gan, zhi);
}

/**
 * 获取指定日干和时辰的时柱信息
 * @param {string} yuanGan - 日元
 * @param {string} riGan - 日干
 * @param {number} shiChen - 时辰索引(0-11)
 * @returns {Object} 时柱信息
 */
export function getShiZhuByRiGanShiChen(yuanGan, riGan, shiChen) {
  const startGan = WU_SHU_DUN[riGan];
  const startGanIdx = TIAN_GAN.indexOf(startGan);
  
  const ganIdx = (startGanIdx + shiChen) % 10;
  const gan = TIAN_GAN[ganIdx];
  const zhi = DI_ZHI[shiChen];
  
  return getZhuInfo(yuanGan, gan, zhi);
}

// ============================================
// 数据增强：将后端简化数据补充为完整命盘数据
// ============================================

/**
 * 计算大运
 * @param {string} yueGan - 月干
 * @param {string} yueZhi - 月支
 * @param {string} riGan - 日干（用于十神计算）
 * @param {string} shunNi - "顺" | "逆"
 * @param {number} startYear - 起运年份
 * @returns {Object} 大运数据，key为年份
 */
function calculateDaYun(yueGan, yueZhi, riGan, shunNi, startYear) {
  const step = shunNi === "顺" ? 1 : -1;
  const ganIdx = TIAN_GAN.indexOf(yueGan);
  const zhiIdx = DI_ZHI.indexOf(yueZhi);

  const daYun = {};
  for (let i = 1; i <= 10; i++) {
    const gIdx = (ganIdx + i * step + 10) % 10;
    const zIdx = (zhiIdx + i * step + 12) % 12;
    const year = startYear + (i - 1) * 10;
    daYun[year] = getZhuInfo(riGan, TIAN_GAN[gIdx], DI_ZHI[zIdx]);
  }
  return daYun;
}

/**
 * 计算流年
 * @param {string} riGan - 日干（用于十神计算）
 * @param {number} startYear - 开始年份
 * @param {number} endYear - 结束年份
 * @returns {Object} 流年数据，key为年份
 */
function calculateLiuNian(riGan, startYear, endYear) {
  const liuNian = {};
  for (let y = startYear; y <= endYear; y++) {
    const [gan, zhi] = getNianGanZhi(y);
    liuNian[y] = getZhuInfo(riGan, gan, zhi);
  }
  return liuNian;
}

/**
 * 将后端简化的八字数据增强为完整命盘数据
 * 后端只返回：四柱干支 + 起运信息
 * 前端补充：四柱详情（藏干+十神）+ 大运 + 流年
 * @param {Object} data - 后端返回的 BaZiResult
 * @returns {Object} 增强后的完整数据
 */
export function enrichBaZiData(data) {
  if (!data || !data.ri_gan) return data;

  const riGan = data.ri_gan;

  // 组装四柱（含藏干+十神）
  data.nian_zhu = getZhuInfo(riGan, data.nian_gan, data.nian_zhi);
  data.yue_zhu = getZhuInfo(riGan, data.yue_gan, data.yue_zhi);
  data.ri_zhu = getZhuInfo(riGan, data.ri_gan, data.ri_zhi);
  data.shi_zhu = getZhuInfo(riGan, data.shi_gan, data.shi_zhi);

  // 计算大运
  if (data.yue_gan && data.yue_zhi && data.shun_ni && data.da_yun_start_year) {
    data.da_yun = calculateDaYun(data.yue_gan, data.yue_zhi, riGan, data.shun_ni, data.da_yun_start_year);
  }

  // 计算流年
  const birthYear = new Date(data.birthday_dt || data.birthday_str).getFullYear();
  data.liu_nian = calculateLiuNian(riGan, birthYear, birthYear + 100);

  return data;
}
